import { createContext, useContext, useState, useEffect } from "react";
import { getData, getVisitData } from "@/Data/api/DpData.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { totalMember, totalFC } from "@/Data/api/MemberData.js";
import { totalDeposit } from "@/Data/api/DepositData.js";
import type { ChildrenProps, DataContextValue, VisitRecord } from "@/types/app";

const DataContext = createContext<DataContextValue | null>(null);

export const DataProvider = ({ children }: ChildrenProps) => {
  const { user } = useAuth();
  const [dpData, setDpData] = useState<unknown[]>([]);
  const [visitData, setVisitData] = useState<VisitRecord[]>([]);
  const [famCount, setFamCount] = useState(0);
  const [memCount, setMemCount] = useState(0);
  const [depCount, setDepCount] = useState(0);
  const [visitCount, setVisitCount] = useState(0);
  const [dpDataDate, setDpDataDate] = useState<string | null>(null);
  const [visitDataDate, setVisitDataDate] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [errorData, setErrorData] = useState<unknown>(null);
  const getLastDate = (date: unknown): string | null => {
    if (typeof date === "string") return date;
    if (Array.isArray(date)) {
      const lastDate = date.flat().at(-1);
      return typeof lastDate === "string" ? lastDate : null;
    }
    return null;
  };
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    const { signal } = controller;
    const fetchData = async () => {
      setLoadingData(true);
      setErrorData(null);
      try {
        const [dp, visit] = await Promise.all([
          getData(user, { signal }),
          getVisitData(user, { signal }),
        ]);
        const mem = await totalMember(user, { signal });
        const fam = await totalFC(user, { signal });
        const deposit = await totalDeposit(user, { signal });

        setDpData(dp?.content ?? []);
        setDpDataDate(getLastDate(dp?.date));
        setVisitData(visit?.content ?? []);
        setVisitCount(visit?.total ?? 0);
        setFamCount(fam ?? 0);
        setMemCount(mem ?? 0);
        setDepCount(deposit ?? 0);

        setVisitDataDate(getLastDate(visit?.date));
      } catch (err) {
        if (!controller.signal.aborted) {
          setErrorData(err);
        }
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [user]);

  return (
    <DataContext.Provider
      value={{
        dpData,
        dpDataDate,
        visitData,
        visitDataDate,
        loadingData,
        errorData,
        visitCount,
        famCount,
        memCount,
        depCount,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used inside DataProvider");
  }
  return context;
};
